/**
 * Componente para renderizar marcadores de nodos en el mapa
 * Usa iconos SVG personalizados desde NodeIcons.tsx
 * @module components/map/NodeMarkers
 */

import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { AnyNode } from '@/types/nodes'
import { NODE_ICONS } from '@/constants'
import { useNodeStore, useUIStore, useFiberStore, useMapHistoryStore } from '@/stores'
import { createNodeIcon } from './NodeIcons'
import { moverNodoConCables } from '@/utils/moverNodoConCables'
import {
  collapseSpiderfy,
  expandSpiderfy,
  groupVisibleNodeIdsByPosition,
  nodePositionKey,
  type SpiderfiedState,
} from '@/utils/mapMarkerSpiderfy'

type DragSnap = {
  before: { lat: number; lng: number }
}

interface NodeMarkersProps {
  map: L.Map
  nodes: AnyNode[]
  onNodeClickForCable?: (nodeId: string) => void
  selectedNodesForCable?: string[]
}

export function NodeMarkers({ map, nodes, onNodeClickForCable, selectedNodesForCable = [] }: NodeMarkersProps) {
  const markersRef = useRef<Map<string, L.Marker>>(new Map())
  const dragSnapRef = useRef<Map<string, DragSnap>>(new Map())
  const spiderfiedRef = useRef<SpiderfiedState | null>(null)
  const selectNode = useNodeStore((state) => state.selectNode)
  const selectedNodeId = useNodeStore((state) => state.selectedNodeId)
  const selectedNodeIds = useUIStore((state) => state.selectedNodeIds)
  const updateNode = useNodeStore((state) => state.updateNode)
  const visibleNodeTypes = useUIStore((state) => state.visibleNodeTypes)
  const nodeMarkerSize = useUIStore((state) => state.nodeMarkerSize)
  const showLabels = useUIStore((state) => state.showLabels)
  const editModeEnabled = useUIStore((state) => state.editModeEnabled)
  const addNotification = useUIStore((state) => state.addNotification)
  
  // Para actualizar cables cuando se mueve un nodo
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  useEffect(() => {
    if (spiderfiedRef.current) {
      collapseSpiderfy(map, spiderfiedRef.current, markersRef.current)
      spiderfiedRef.current = null
    }

    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current.clear()

    const overlapGroups = groupVisibleNodeIdsByPosition(nodes, visibleNodeTypes)

    const collapseSpiderfyIfOpen = () => {
      if (!spiderfiedRef.current) return
      collapseSpiderfy(map, spiderfiedRef.current, markersRef.current)
      spiderfiedRef.current = null
    }

    const onMapOrViewChange = () => collapseSpiderfyIfOpen()
    map.on('click', onMapOrViewChange)
    map.on('zoomend', onMapOrViewChange)
    map.on('moveend', onMapOrViewChange)

    // Crear marcadores para cada nodo
    nodes.forEach((node) => {
      // Filtrar por visibilidad
      if (!visibleNodeTypes.has(node.tipo)) {
        return
      }

      const isSelected = node.id === selectedNodeId
      const isSelectedForCable = selectedNodesForCable.includes(node.id)
      const inMultiMapSelection = selectedNodeIds.includes(node.id)
      const cableOrderNumber = isSelectedForCable
        ? selectedNodesForCable.indexOf(node.id) + 1
        : undefined

      // Nombre del nodo para popup y notificaciones
      const nodeName =
        (node as any).nombre || (node as any).refCode || node.id

      // Crear icono SVG personalizado
      const icon = createNodeIcon(node.tipo, {
        size: nodeMarkerSize * 2.5,
        isSelected,
        isSelectedForCable,
        cableOrderNumber,
        inMultiSelection: inMultiMapSelection,
        customIcon: (node as any).customIcon || (node as any).config?.customIcon,
      })

      // Crear marcador con el icono SVG
      const marker = L.marker([node.lat, node.lng], {
        icon,
        draggable: editModeEnabled,
      })

      // Handler para arrastrar nodo (solo si editModeEnabled)
      if (editModeEnabled) {
        marker.on('dragstart', () => {
          marker.closePopup()
          marker.closeTooltip()
          dragSnapRef.current.set(node.id, {
            before: { lat: node.lat, lng: node.lng },
          })
        })

        marker.on('dragend', async (e) => {
          const snap = dragSnapRef.current.get(node.id)
          dragSnapRef.current.delete(node.id)
          if (!snap) return

          const newLatLng = (e.target as L.Marker).getLatLng()
          if (newLatLng.lat === snap.before.lat && newLatLng.lng === snap.before.lng) {
            return
          }

          try {
            const result = await moverNodoConCables(node, newLatLng, {
              updateNode: (n) => updateNode(n as any),
              updateCable: (c) => updateCable(c as any),
              cables: cables as any,
            })

            useMapHistoryStore.getState().pushNodeMove({
              nodeId: result.nodeId,
              before: result.before,
              after: result.after,
              cables: result.cables,
            })

            addNotification('success', `Nodo "${nodeName}" movido correctamente`)
          } catch (error) {
            console.error('Error al mover nodo:', error)
            marker.setLatLng([snap.before.lat, snap.before.lng])
            addNotification(
              'error',
              'No se pudo mover el nodo (se revirtió la posición)'
            )
          }
        })
      }

      // Popup con información básica
      const popupContent = `
        <div class="p-2 min-w-[180px]">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-2xl">${NODE_ICONS[node.tipo]}</span>
            <div>
              <p class="font-bold text-sm">${nodeName}</p>
              ${(node as any).refCode ? `<p class="text-xs font-mono text-blue-600">${(node as any).refCode}</p>` : ''}
              <p class="text-xs text-gray-600">${node.tipo}</p>
            </div>
          </div>
          ${
            (node as any).descripcion
              ? `<p class="text-xs text-gray-500 mb-2">${(node as any).descripcion}</p>`
              : ''
          }
          <div class="text-xs space-y-1">
            <p><strong>Lat:</strong> ${node.lat.toFixed(6)}</p>
            <p><strong>Lng:</strong> ${node.lng.toFixed(6)}</p>
          </div>
          <button
            class="mt-2 w-full px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            onclick="window.selectNode('${node.id}')"
          >
            Ver detalles
          </button>
        </div>
      `

      // En modo de trazado/edición de cable, no enlazamos popup para que el
      // click en el poste se registre inmediatamente sin abrir detalles.
      if (!onNodeClickForCable) {
        marker.bindPopup(popupContent, {
          maxWidth: 280,
          className: 'custom-popup',
        })
      }

      // Tooltip con nombre (si showLabels está activo)
      if (showLabels) {
        marker.bindTooltip(nodeName, {
          permanent: true,
          direction: 'top',
          offset: [0, -nodeMarkerSize],
          className: 'custom-tooltip bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-lg text-xs font-medium',
        })
      }

      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e)

        const runSelect = () => {
          if (onNodeClickForCable) {
            onNodeClickForCable(node.id)
            return
          }
          const oe = e.originalEvent
          const multi = !!(oe.ctrlKey || oe.metaKey)
          const ui = useUIStore.getState()
          if (multi) {
            ui.toggleNodeInMapSelection(node.id)
          } else {
            ui.setMapSelection([node.id])
          }
          selectNode(node.id)
        }

        // En modo de trazado/edición de ruta, saltamos spiderfy para que el
        // click simple agregue/quite el poste de inmediato, aun cuando esté
        // superpuesto con otros marcadores.
        if (onNodeClickForCable) {
          runSelect()
          return
        }

        if (editModeEnabled) {
          runSelect()
          return
        }

        const posKey = nodePositionKey(node.lat, node.lng)
        const groupIds = overlapGroups.get(posKey) ?? [node.id]

        if (groupIds.length === 1) {
          runSelect()
          return
        }

        const expandedHere = spiderfiedRef.current?.groupKey === posKey

        if (!expandedHere) {
          if (spiderfiedRef.current) {
            collapseSpiderfy(map, spiderfiedRef.current, markersRef.current)
            spiderfiedRef.current = null
          }
          const radiusPx = Math.max(36, nodeMarkerSize * 4)
          const state = expandSpiderfy(
            map,
            markersRef.current,
            groupIds,
            posKey,
            radiusPx
          )
          if (state) {
            spiderfiedRef.current = state
            setTimeout(() => {
              for (const id of groupIds) {
                markersRef.current.get(id)?.closePopup()
              }
            }, 0)
          }
          return
        }

        collapseSpiderfyIfOpen()
        runSelect()
      })

      // Agregar al mapa
      marker.addTo(map)

      // Guardar referencia
      markersRef.current.set(node.id, marker)
    })

    // Exponer función global para el popup
    ;(window as any).selectNode = (id: string) => {
      useUIStore.getState().setMapSelection([id])
      selectNode(id)
    }

    return () => {
      map.off('click', onMapOrViewChange)
      map.off('zoomend', onMapOrViewChange)
      map.off('moveend', onMapOrViewChange)
      if (spiderfiedRef.current) {
        collapseSpiderfy(map, spiderfiedRef.current, markersRef.current)
        spiderfiedRef.current = null
      }
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current.clear()
    }
  }, [
    map,
    nodes,
    selectedNodeId,
    visibleNodeTypes,
    nodeMarkerSize,
    showLabels,
    selectNode,
    onNodeClickForCable,
    selectedNodesForCable,
    selectedNodeIds,
    editModeEnabled,
    updateNode,
    addNotification,
    cables,
    updateCable,
  ])

  return null
}
